/**
 * Created by neo on 23.12.22.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { Container } from 'reactstrap';
import { useParams, useLocation } from 'react-router-dom';
import { ExploreContentBuilder, KinasticContent } from '../../../Model/Explore/ExploreContentBuilder';
import { ExploreCategory } from '../../../Model/Explore/ExploreCategory';
import { useRootStore } from '../../../Store/useRootStore';
import { ExploreContentViewRenderer } from '../../../Components/EntryView/ExploreContentViewRenderer';
import { ExploreContentViewCategoryRenderer } from '../../Academy/View/CategoryView/ExploreContentViewCategoryRenderer';
import { RelatedEntriesView } from '../../../Components/RelatedEntriesView';
import { EventEntry, EventEntryApi } from '../../../Model/Coach/Schedule/EventEntry';
import { MicroCycleDay, MicroCycleDayApi } from '../../../Model/Coach/Program/MicroCycleDay';
import { WorkoutLogApi } from '../../../Model/WorkoutLog/WorkoutLog';
import { ProgramWorkoutView } from './ProgramWorkoutView';
import { ContentNotFound } from '../../../Components/EntryView/ContentNotFound';

export type MoveContentViewScreenProps = {};

type LocationState = {
  microCycleDayId?: string;
  workoutLogId?: string;
};

export const MoveContentViewScreen: React.FC<MoveContentViewScreenProps> = observer((props) => {
  const { analytics, workoutViewStore } = useRootStore();
  const location = useLocation();
  const locationState = location.state as LocationState | undefined;

  const [content, setContent] = useState<KinasticContent | undefined>();
  const [lastError, setLastError] = useState<unknown>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMicroCycleDay, setLoadingMicroCycleDay] = useState(!!locationState?.microCycleDayId);
  const [parentEvent, setParentEvent] = useState<EventEntry | undefined>();
  const [currentEvent, setCurrentEvent] = useState<EventEntry | undefined>();
  const [microCycleDay, setMicroCycleDay] = useState<MicroCycleDay | undefined>();

  const exploreEntry = content && 'backgroundImage' in content ? content : undefined;

  const { contentId } = useParams<{ contentId: string }>();

  // Load microCycleDay if provided in navigation state (for program workouts)
  useEffect(() => {
    if (locationState?.microCycleDayId) {
      setLoadingMicroCycleDay(true);
      MicroCycleDayApi.findOne(locationState.microCycleDayId)
        .then((day) => setMicroCycleDay(day))
        .catch((e) => console.warn('Failed to load microCycleDay:', e))
        .finally(() => setLoadingMicroCycleDay(false));
    } else {
      setLoadingMicroCycleDay(false);
    }
  }, [locationState?.microCycleDayId]);

  // Resume existing workout log if provided
  useEffect(() => {
    if (locationState?.workoutLogId) {
      WorkoutLogApi.get(locationState.workoutLogId)
        .then((log) => {
          if (log) {
            workoutViewStore.restoreWorkout(log);
          }
        })
        .catch((e) => console.warn('Failed to restore workout log:', e));
    }
  }, [locationState?.workoutLogId, workoutViewStore]);

  useEffect(() => {
    setContent(undefined);
    setLastError(undefined);
    if (contentId) {
      setLoading(true);
      ExploreContentBuilder.findOne(contentId)
        .then((res) => setContent(res))
        .catch((e: unknown) => {
          // If content not found but we have microCycleDayId, it's a program workout
          // The workout will be loaded from microCycleDay instead
          if (!locationState?.microCycleDayId) {
            setLastError(e);
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [contentId, locationState?.microCycleDayId]);

  /**
   * Load an existing associated event
   */
  useEffect(() => {
    if (exploreEntry) {
      EventEntryApi.findParent({ objectId: exploreEntry.id, parent: true, size: 1 }).then((result) =>
        setParentEvent(result),
      );
    }
  }, [exploreEntry]);

  useEffect(() => {
    if (content) {
      const type = content.type === 'recipe' ? 'recipe' : content.type;
      analytics.logSelectContent(type, content.id);
    }
  }, [analytics, content]);

  // Render program workout if we have microCycleDay but no ExploreEntry content
  if (!content && microCycleDay) {
    return <ProgramWorkoutView microCycleDay={microCycleDay} />;
  }

  if (content) {
    if ('contentType' in content && content.contentType === 'category' && 'identifier' in content) {
      return (
        <Container>
          <ExploreContentViewCategoryRenderer category={content as ExploreCategory} linkPrefix={'/move/'} />
        </Container>
      );
    }
    return (
      <React.Fragment>
        <ExploreContentViewRenderer entry={content} parentEvent={parentEvent} selectedEvent={currentEvent} />
        {exploreEntry && <RelatedEntriesView entry={exploreEntry} linkPrefix={'/move/'} />}
      </React.Fragment>
    );
  }

  // Show loading state (either content or microCycleDay still loading)
  if (loading || loadingMicroCycleDay) {
    return <Container style={{ padding: '48px', textAlign: 'center' }}>Loading...</Container>;
  }

  return lastError && !microCycleDay ? <ContentNotFound /> : null;
});
